// =============================================================================
// VouchersScreen — list, create, bulk-generate, edit, and deactivate
// discount codes; manually mark them used
// =============================================================================
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { formatError } from '../lib/errors';
import { formatVenueDateTime } from '../lib/time';
import {
  listDiscountCodes,
  createDiscountCode,
  createDiscountCodesBulk,
  updateDiscountCode,
  markDiscountCodeUsed,
  deactivateDiscountCode,
  voucherStatusOf,
  type DiscountCodeRow,
  type DiscountKind,
  type BulkCreateResult,
} from '../lib/vouchers';
import { fetchWalkinRefData, type BookingExperience } from '../lib/walkin-data';
import { resendVoucherEmail } from '../api/resend-voucher-email';

export function VouchersScreen() {
  const { operator } = useAuth();
  const [rows, setRows] = useState<DiscountCodeRow[]>([]);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [editRow, setEditRow] = useState<DiscountCodeRow | null>(null);
  const [experiences, setExperiences] = useState<BookingExperience[]>([]);

  const load = useCallback(async () => {
    setError(null);
    try {
      const list = await listDiscountCodes(includeInactive);
      setRows(list);
    } catch (e) {
      setError(formatError(e));
    } finally {
      setLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  // Experiences are needed by both the create and bulk modals — fetch once.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const ref = await fetchWalkinRefData();
        if (!cancelled) setExperiences(ref.experiences);
      } catch {
        // Non-fatal — modals fall back to a single "Alle experiences" option.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Client-side filter over the loaded list: code, notes, experience and
  // (for bought gift vouchers) the buyer's name / e-mail.
  const visibleRows = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.code, r.notes, r.experience_name, r.buyer_email, r.buyer_name]
        .some((v) => (v ?? '').toLowerCase().includes(q)),
    );
  }, [rows, filter]);

  const handleDeactivate = useCallback(
    async (id: string) => {
      if (!operator?.id) return;
      if (!window.confirm('Voucher deactiveren? Bestaande inwisselingen blijven behouden.')) return;
      try {
        await deactivateDiscountCode(operator.id, id);
        void load();
      } catch (e) {
        setError(formatError(e));
      }
    },
    [operator?.id, load],
  );

  const handleMarkUsed = useCallback(
    async (row: DiscountCodeRow, undo: boolean) => {
      if (!operator?.id) return;
      const msg = undo
        ? `Handmatige markering van ${row.code} ongedaan maken?`
        : `${row.code} markeren als gebruikt? Het telt dan als een extra inwisseling.`;
      if (!window.confirm(msg)) return;
      try {
        await markDiscountCodeUsed(operator.id, row.id, undo);
        void load();
      } catch (e) {
        setError(formatError(e));
      }
    },
    [operator?.id, load],
  );

  return (
    <div className="screen vouchers-screen">
      <div className="vouchers-toolbar">
        <h1>Vouchers</h1>
        <div className="vouchers-toolbar-actions">
          <input
            type="search"
            className="vouchers-filter"
            placeholder="Zoek code, koper, notitie…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <label className="vouchers-toggle">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
            <span>Toon inactief</span>
          </label>
          <button
            className="btn-link"
            onClick={() => setShowBulk(true)}
          >
            Bulk genereren
          </button>
          <button
            className="walkin-primary-btn"
            onClick={() => setShowCreate(true)}
          >
            + Nieuwe voucher
          </button>
        </div>
      </div>

      {error ? <div className="error-card">{error}</div> : null}
      {loading ? <div className="muted">Laden…</div> : null}

      {!loading && rows.length === 0 ? (
        <div className="muted vouchers-empty">Nog geen vouchers aangemaakt.</div>
      ) : null}

      {!loading && rows.length > 0 && visibleRows.length === 0 ? (
        <div className="muted vouchers-empty">Geen vouchers voor "{filter}".</div>
      ) : null}

      {visibleRows.length > 0 ? (
        <table className="vouchers-table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Korting</th>
              <th>Koper</th>
              <th>Voor</th>
              <th>Geldig</th>
              <th>Gebruik</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((r) => (
              <VoucherRow
                key={r.id}
                row={r}
                onEdit={setEditRow}
                onMarkUsed={handleMarkUsed}
                onDeactivate={handleDeactivate}
              />
            ))}
          </tbody>
        </table>
      ) : null}

      {showCreate ? (
        <CreateVoucherModal
          operatorId={operator?.id ?? ''}
          experiences={experiences}
          onClose={(created) => {
            setShowCreate(false);
            if (created) void load();
          }}
        />
      ) : null}

      {showBulk ? (
        <BulkVoucherModal
          operatorId={operator?.id ?? ''}
          experiences={experiences}
          onClose={(created) => {
            setShowBulk(false);
            if (created) void load();
          }}
        />
      ) : null}

      {editRow ? (
        <EditVoucherModal
          operatorId={operator?.id ?? ''}
          row={editRow}
          experiences={experiences}
          onClose={(saved) => {
            setEditRow(null);
            if (saved) void load();
          }}
        />
      ) : null}
    </div>
  );
}

function VoucherRow({
  row,
  onEdit,
  onMarkUsed,
  onDeactivate,
}: {
  row: DiscountCodeRow;
  onEdit: (row: DiscountCodeRow) => void;
  onMarkUsed: (row: DiscountCodeRow, undo: boolean) => void;
  onDeactivate: (id: string) => void;
}) {
  // GIFT- codes were minted by a gift-voucher purchase and have a buyer on
  // file — those can have their voucher email re-sent. TEGOED- refund codes
  // and hand-made codes have no linked purchase.
  const isGiftPurchase = row.code.startsWith('GIFT-');
  const [resendBusy, setResendBusy] = useState(false);
  const [resendNotice, setResendNotice] = useState<{
    kind: 'ok' | 'err';
    message: string;
  } | null>(null);
  const handleResend = useCallback(async () => {
    setResendBusy(true);
    setResendNotice(null);
    const result = await resendVoucherEmail({ codeId: row.id });
    setResendBusy(false);
    if (!result.ok || !result.data) {
      const msg = result.error?.message ?? result.error?.error ?? 'onbekende fout';
      setResendNotice({ kind: 'err', message: `Mislukt: ${msg}` });
      return;
    }
    setResendNotice({ kind: 'ok', message: 'Verstuurd ✓' });
  }, [row.id]);

  const discountStr =
    row.kind === 'percent'
      ? `${row.percent_off}%`
      : `€ ${((row.flat_off_cents ?? 0) / 100).toFixed(2)}`;

  const validity =
    !row.valid_from && !row.valid_until
      ? 'Altijd'
      : `${
          row.valid_from ? formatVenueDateTime(row.valid_from) : '—'
        } → ${row.valid_until ? formatVenueDateTime(row.valid_until) : '∞'}`;

  const usage =
    row.max_uses == null
      ? `${row.uses_count}× / ∞`
      : `${row.uses_count}× / ${row.max_uses}×`;

  const status = voucherStatusOf(row);

  return (
    <tr>
      <td>
        <Link to={`/voucher/${row.code}`} className="voucher-code voucher-code-link" title="Geschiedenis en inwisselingen">
          {row.code}
        </Link>
        {row.notes ? (
          <div className="muted voucher-notes">{row.notes}</div>
        ) : null}
      </td>
      <td>{discountStr}</td>
      <td className="muted voucher-buyer">
        {row.buyer_email ? (
          <>
            {row.buyer_name ? <div>{row.buyer_name}</div> : null}
            <div className="voucher-buyer-email">{row.buyer_email}</div>
          </>
        ) : (
          '—'
        )}
      </td>
      <td className="muted">
        {row.experience_name ?? <em>alle</em>}
      </td>
      <td className="muted">{validity}</td>
      <td>{usage}</td>
      <td>
        <span className={`voucher-pill ${status.className}`}>{status.label}</span>
      </td>
      <td>
        <div className="voucher-actions">
          <button className="btn-link" onClick={() => onEdit(row)}>
            Bewerken
          </button>
          {isGiftPurchase ? (
            <>
              <button
                className="btn-link"
                onClick={() => void handleResend()}
                disabled={resendBusy}
                title="Verstuur de cadeaubon-mail (code + geldigheid) opnieuw naar de koper"
              >
                {resendBusy ? 'Versturen…' : 'Bon opnieuw mailen'}
              </button>
              {resendNotice ? (
                <span
                  className={
                    resendNotice.kind === 'ok'
                      ? 'resend-notice resend-notice-ok'
                      : 'resend-notice resend-notice-err'
                  }
                >
                  {resendNotice.message}
                </span>
              ) : null}
            </>
          ) : null}
          {row.is_active && !row.exhausted ? (
            <button className="btn-link" onClick={() => onMarkUsed(row, false)}>
              Markeer gebruikt
            </button>
          ) : null}
          {row.exhausted && row.uses_count > 0 ? (
            <button className="btn-link" onClick={() => onMarkUsed(row, true)}>
              Herstel gebruik
            </button>
          ) : null}
          {row.is_active ? (
            <button
              className="btn-link voucher-deactivate"
              onClick={() => onDeactivate(row.id)}
            >
              Deactiveren
            </button>
          ) : null}
        </div>
      </td>
    </tr>
  );
}

// -----------------------------------------------------------------------------
// Shared form fields (kind / value / max_uses / validity / experience / notes)
// -----------------------------------------------------------------------------
type MaxUsesMode = 'unlimited' | 'single' | 'n';

interface VoucherFormState {
  kind: DiscountKind;
  percentOff: string;
  flatOffEur: string;
  maxUsesMode: MaxUsesMode;
  maxUsesN: string;
  validFrom: string;
  validUntil: string;
  notes: string;
  experienceId: string;       // '' = universal
}

function initialFormState(): VoucherFormState {
  return {
    kind: 'percent',
    percentOff: '10',
    flatOffEur: '5.00',
    maxUsesMode: 'single',
    maxUsesN: '5',
    validFrom: '',
    validUntil: '',
    notes: '',
    experienceId: '',
  };
}

function isoToLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formStateFromRow(row: DiscountCodeRow): VoucherFormState {
  return {
    kind: row.kind,
    percentOff: String(row.percent_off ?? 10),
    flatOffEur: row.flat_off_cents != null ? (row.flat_off_cents / 100).toFixed(2) : '5.00',
    maxUsesMode:
      row.max_uses == null ? 'unlimited' : row.max_uses === 1 ? 'single' : 'n',
    maxUsesN: String(row.max_uses ?? 5),
    validFrom: isoToLocalInput(row.valid_from),
    validUntil: isoToLocalInput(row.valid_until),
    notes: row.notes ?? '',
    experienceId: row.experience_id ?? '',
  };
}

function computedMaxUsesFrom(state: VoucherFormState): number | null {
  if (state.maxUsesMode === 'unlimited') return null;
  if (state.maxUsesMode === 'single') return 1;
  const n = Number(state.maxUsesN);
  return Number.isInteger(n) && n >= 1 ? n : 1;
}

function isShapeValid(state: VoucherFormState): boolean {
  return state.kind === 'percent'
    ? Number(state.percentOff) >= 1 && Number(state.percentOff) <= 100
    : Number(state.flatOffEur) > 0;
}

function VoucherFormFields({
  state,
  onChange,
  experiences,
}: {
  state: VoucherFormState;
  onChange: (next: VoucherFormState) => void;
  experiences: BookingExperience[];
}) {
  const set = (patch: Partial<VoucherFormState>) =>
    onChange({ ...state, ...patch });

  return (
    <>
      <div className="walkin-row">
        <label className="walkin-field">
          <span>Type</span>
          <select
            value={state.kind}
            onChange={(e) => set({ kind: e.target.value as DiscountKind })}
          >
            <option value="percent">Percentage</option>
            <option value="flat">Vast bedrag</option>
          </select>
        </label>
        {state.kind === 'percent' ? (
          <label className="walkin-field">
            <span>Korting (%)</span>
            <input
              type="number"
              min={1}
              max={100}
              value={state.percentOff}
              onChange={(e) => set({ percentOff: e.target.value })}
            />
          </label>
        ) : (
          <label className="walkin-field">
            <span>Korting (€)</span>
            <input
              type="number"
              min={0.01}
              step="0.01"
              value={state.flatOffEur}
              onChange={(e) => set({ flatOffEur: e.target.value })}
            />
          </label>
        )}
      </div>

      <label className="walkin-field">
        <span>Geldig voor</span>
        <select
          value={state.experienceId}
          onChange={(e) => set({ experienceId: e.target.value })}
        >
          <option value="">Alle experiences</option>
          {experiences.map((e) => (
            <option key={e.id} value={e.id}>{e.name}</option>
          ))}
        </select>
      </label>

      <fieldset className="cancel-refund-options">
        <legend>Aantal keer bruikbaar</legend>
        <label className="cancel-radio">
          <input
            type="radio"
            checked={state.maxUsesMode === 'single'}
            onChange={() => set({ maxUsesMode: 'single' })}
          />
          <span>Eenmalig (1×)</span>
        </label>
        <label className="cancel-radio">
          <input
            type="radio"
            checked={state.maxUsesMode === 'n'}
            onChange={() => set({ maxUsesMode: 'n' })}
          />
          <span>Aantal keer:</span>
          {state.maxUsesMode === 'n' ? (
            <input
              type="number"
              min={1}
              className="cancel-partial-input"
              value={state.maxUsesN}
              onChange={(e) => set({ maxUsesN: e.target.value })}
            />
          ) : null}
        </label>
        <label className="cancel-radio">
          <input
            type="radio"
            checked={state.maxUsesMode === 'unlimited'}
            onChange={() => set({ maxUsesMode: 'unlimited' })}
          />
          <span>Onbeperkt</span>
        </label>
      </fieldset>

      <div className="walkin-row">
        <label className="walkin-field">
          <span>Geldig vanaf (optioneel)</span>
          <input
            type="datetime-local"
            value={state.validFrom}
            onChange={(e) => set({ validFrom: e.target.value })}
          />
        </label>
        <label className="walkin-field">
          <span>Geldig t/m (optioneel)</span>
          <input
            type="datetime-local"
            value={state.validUntil}
            onChange={(e) => set({ validUntil: e.target.value })}
          />
        </label>
      </div>

      <label className="walkin-field">
        <span>Notitie (intern, optioneel)</span>
        <textarea
          rows={2}
          value={state.notes}
          onChange={(e) => set({ notes: e.target.value })}
          placeholder="Bijv. lancering mei 2026"
        />
      </label>
    </>
  );
}

function toIsoOrNull(s: string): string | null {
  return s ? new Date(s).toISOString() : null;
}

// -----------------------------------------------------------------------------
// Single-code create modal
// -----------------------------------------------------------------------------
function CreateVoucherModal({
  operatorId,
  experiences,
  onClose,
}: {
  operatorId: string;
  experiences: BookingExperience[];
  onClose: (created: boolean) => void;
}) {
  const [code, setCode] = useState('');
  const [form, setForm] = useState<VoucherFormState>(initialFormState);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const valid =
    code.trim().length > 0 &&
    /^[A-Za-z0-9_\-]+$/.test(code.trim()) &&
    isShapeValid(form);

  const handleSubmit = useCallback(async () => {
    if (!valid || submitting || !operatorId) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await createDiscountCode(operatorId, {
        code: code.trim(),
        kind: form.kind,
        percent_off: form.kind === 'percent' ? Number(form.percentOff) : null,
        flat_off_cents:
          form.kind === 'flat' ? Math.round(Number(form.flatOffEur) * 100) : null,
        max_uses: computedMaxUsesFrom(form),
        valid_from: toIsoOrNull(form.validFrom),
        valid_until: toIsoOrNull(form.validUntil),
        notes: form.notes.trim().length > 0 ? form.notes.trim() : null,
        experience_id: form.experienceId.length > 0 ? form.experienceId : null,
      });
      onClose(true);
    } catch (e) {
      setSubmitError(formatError(e));
    } finally {
      setSubmitting(false);
    }
  }, [valid, submitting, operatorId, code, form, onClose]);

  return (
    <div className="dep-modal-backdrop" onClick={() => { /* ✕ / Esc only */ }}>
      <div className="dep-modal voucher-modal" onClick={(e) => e.stopPropagation()}>
        <header className="dep-modal-head">
          <div><h2>Nieuwe voucher</h2></div>
          <button
            className="btn-link dep-modal-close"
            onClick={() => !submitting && onClose(false)}
            aria-label="Sluiten"
          >✕</button>
        </header>

        <div className="dep-modal-body">
          <div className="walkin-step">
            <label className="walkin-field">
              <span>Code</span>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="WELCOME10"
                autoFocus
              />
            </label>

            <VoucherFormFields
              state={form}
              onChange={setForm}
              experiences={experiences}
            />

            {submitError ? (
              <div className="error-card walkin-submit-error">{submitError}</div>
            ) : null}

            <div className="walkin-step-actions">
              <button
                className="btn-link"
                disabled={submitting}
                onClick={() => onClose(false)}
              >Annuleren</button>
              <button
                className="walkin-primary-btn"
                disabled={!valid || submitting}
                onClick={() => void handleSubmit()}
              >{submitting ? 'Bezig…' : 'Voucher aanmaken'}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Edit modal — prefilled from the row; can also (her)activate
// -----------------------------------------------------------------------------
function EditVoucherModal({
  operatorId,
  row,
  experiences,
  onClose,
}: {
  operatorId: string;
  row: DiscountCodeRow;
  experiences: BookingExperience[];
  onClose: (saved: boolean) => void;
}) {
  const [code, setCode] = useState(row.code);
  const [form, setForm] = useState<VoucherFormState>(() => formStateFromRow(row));
  const [isActive, setIsActive] = useState(row.is_active);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const valid =
    code.trim().length > 0 &&
    /^[A-Za-z0-9_\-]+$/.test(code.trim()) &&
    isShapeValid(form);

  const usage =
    row.max_uses == null
      ? `${row.uses_count}× / ∞`
      : `${row.uses_count}× / ${row.max_uses}×`;

  const handleSubmit = useCallback(async () => {
    if (!valid || submitting || !operatorId) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await updateDiscountCode(operatorId, row.id, {
        code: code.trim(),
        kind: form.kind,
        percent_off: form.kind === 'percent' ? Number(form.percentOff) : null,
        flat_off_cents:
          form.kind === 'flat' ? Math.round(Number(form.flatOffEur) * 100) : null,
        max_uses: computedMaxUsesFrom(form),
        valid_from: toIsoOrNull(form.validFrom),
        valid_until: toIsoOrNull(form.validUntil),
        notes: form.notes.trim().length > 0 ? form.notes.trim() : null,
        experience_id: form.experienceId.length > 0 ? form.experienceId : null,
        is_active: isActive,
      });
      onClose(true);
    } catch (e) {
      setSubmitError(formatError(e));
    } finally {
      setSubmitting(false);
    }
  }, [valid, submitting, operatorId, row.id, code, form, isActive, onClose]);

  return (
    <div className="dep-modal-backdrop" onClick={() => { /* ✕ / Esc only */ }}>
      <div className="dep-modal voucher-modal" onClick={(e) => e.stopPropagation()}>
        <header className="dep-modal-head">
          <div><h2>Voucher bewerken</h2></div>
          <button
            className="btn-link dep-modal-close"
            onClick={() => !submitting && onClose(false)}
            aria-label="Sluiten"
          >✕</button>
        </header>

        <div className="dep-modal-body">
          <div className="walkin-step">
            <label className="walkin-field">
              <span>Code</span>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
              />
            </label>

            <VoucherFormFields
              state={form}
              onChange={setForm}
              experiences={experiences}
            />

            <div className="muted">Gebruik tot nu toe: {usage}</div>

            <label className="vouchers-toggle">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
              <span>Actief</span>
            </label>

            {submitError ? (
              <div className="error-card walkin-submit-error">{submitError}</div>
            ) : null}

            <div className="walkin-step-actions">
              <button
                className="btn-link"
                disabled={submitting}
                onClick={() => onClose(false)}
              >Annuleren</button>
              <button
                className="walkin-primary-btn"
                disabled={!valid || submitting}
                onClick={() => void handleSubmit()}
              >{submitting ? 'Bezig…' : 'Opslaan'}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Bulk-generator modal
// -----------------------------------------------------------------------------
function BulkVoucherModal({
  operatorId,
  experiences,
  onClose,
}: {
  operatorId: string;
  experiences: BookingExperience[];
  onClose: (created: boolean) => void;
}) {
  const [count, setCount] = useState('100');
  const [prefix, setPrefix] = useState('');
  const [form, setForm] = useState<VoucherFormState>(initialFormState);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<BulkCreateResult | null>(null);
  const [copied, setCopied] = useState(false);

  const numericCount = useMemo(() => {
    const n = Number(count);
    return Number.isInteger(n) ? n : NaN;
  }, [count]);

  const valid =
    Number.isFinite(numericCount) &&
    numericCount >= 1 &&
    numericCount <= 1000 &&
    (prefix === '' || /^[A-Za-z0-9_\-]+$/.test(prefix)) &&
    isShapeValid(form);

  const handleGenerate = useCallback(async () => {
    if (!valid || submitting || !operatorId) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const r = await createDiscountCodesBulk(operatorId, {
        count: numericCount,
        prefix: prefix.length > 0 ? prefix : null,
        kind: form.kind,
        percent_off: form.kind === 'percent' ? Number(form.percentOff) : null,
        flat_off_cents:
          form.kind === 'flat' ? Math.round(Number(form.flatOffEur) * 100) : null,
        max_uses: computedMaxUsesFrom(form),
        valid_from: toIsoOrNull(form.validFrom),
        valid_until: toIsoOrNull(form.validUntil),
        notes: form.notes.trim().length > 0 ? form.notes.trim() : null,
        experience_id: form.experienceId.length > 0 ? form.experienceId : null,
      });
      setResult(r);
    } catch (e) {
      setSubmitError(formatError(e));
    } finally {
      setSubmitting(false);
    }
  }, [valid, submitting, operatorId, numericCount, prefix, form]);

  const copyAll = useCallback(async () => {
    if (!result) return;
    const txt = result.codes.map((c) => c.code).join('\n');
    try {
      await navigator.clipboard.writeText(txt);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard API not available — operator can select-all manually */
    }
  }, [result]);

  return (
    <div className="dep-modal-backdrop" onClick={() => { /* ✕ / Esc only */ }}>
      <div className="dep-modal voucher-modal" onClick={(e) => e.stopPropagation()}>
        <header className="dep-modal-head">
          <div><h2>Bulk vouchers genereren</h2></div>
          <button
            className="btn-link dep-modal-close"
            onClick={() => onClose(result != null)}
            aria-label="Sluiten"
          >✕</button>
        </header>

        <div className="dep-modal-body">
          {result ? (
            <div className="walkin-step">
              <div className="walkin-success-title">
                ✓ {result.created_count} codes aangemaakt
              </div>
              <p className="muted">
                Kopieer de lijst hieronder en bewaar 'm — codes zijn ook
                op de vouchers-pagina te vinden.
              </p>
              <textarea
                readOnly
                rows={Math.min(15, result.codes.length + 1)}
                className="bulk-result-textarea"
                value={result.codes.map((c) => c.code).join('\n')}
                onFocus={(e) => e.currentTarget.select()}
              />
              <div className="walkin-step-actions">
                <button
                  className="btn-link"
                  onClick={() => void copyAll()}
                >
                  {copied ? 'Gekopieerd ✓' : 'Kopieer alles'}
                </button>
                <button
                  className="walkin-primary-btn"
                  onClick={() => onClose(true)}
                >Sluiten</button>
              </div>
            </div>
          ) : (
            <div className="walkin-step">
              <div className="walkin-row">
                <label className="walkin-field">
                  <span>Aantal codes</span>
                  <input
                    type="number"
                    min={1}
                    max={1000}
                    value={count}
                    onChange={(e) => setCount(e.target.value)}
                    autoFocus
                  />
                </label>
                <label className="walkin-field">
                  <span>Prefix (optioneel)</span>
                  <input
                    type="text"
                    value={prefix}
                    onChange={(e) => setPrefix(e.target.value.toUpperCase())}
                    placeholder="GIFT-"
                    maxLength={16}
                  />
                </label>
              </div>

              <VoucherFormFields
                state={form}
                onChange={setForm}
                experiences={experiences}
              />

              {submitError ? (
                <div className="error-card walkin-submit-error">{submitError}</div>
              ) : null}

              <div className="walkin-step-actions">
                <button
                  className="btn-link"
                  disabled={submitting}
                  onClick={() => onClose(false)}
                >Annuleren</button>
                <button
                  className="walkin-primary-btn"
                  disabled={!valid || submitting}
                  onClick={() => void handleGenerate()}
                >
                  {submitting
                    ? 'Bezig…'
                    : `Genereer ${numericCount || ''} ${numericCount === 1 ? 'code' : 'codes'}`}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
